// The key half of §13, on the client.
//
// Federated sign-in proves which account this is. This produces the key that
// decrypts the archive, and the two must not be conflated: an OIDC token is a
// signed assertion, not a secret, and the issuer can mint one without the
// device. Nothing derived from it could be an encryption key.
//
// Normative definitions live in contracts/FORMATS.md §1. The vectors in
// contracts/vectors.json are what keep this and the Rust core byte-identical.

const enc = new TextEncoder();
const subtle = () => globalThis.crypto.subtle;

export const toHex = (b: Uint8Array) =>
  Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
export const fromHex = (h: string) =>
  new Uint8Array((h.match(/../g) ?? []).map((x) => parseInt(x, 16)));
export const toB64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
export const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function hkdf(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array) {
  const key = await subtle().importKey('raw', ikm as BufferSource, 'HKDF', false, ['deriveBits']);
  return new Uint8Array(await subtle().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: info as BufferSource }, key, 256));
}

/** contracts/FORMATS.md §1.1 */
export function kek(secret: Uint8Array, salt: Uint8Array, method: 'passkey' | 'recovery') {
  return hkdf(secret, salt, enc.encode(`wa-archive/v1/kek/${method}`));
}

/**
 * contracts/FORMATS.md §1.2 — the account key is random and *wrapped*, never
 * derived. A passkey and a recovery code that derived different keys would
 * each be able to read only what the other had not written.
 */
export async function wrapAccountKey(accountKey: Uint8Array, kekBytes: Uint8Array) {
  if (accountKey.length !== 32) throw new Error('account key must be 32 bytes');
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await subtle().importKey('raw', kekBytes as BufferSource, 'AES-GCM', false, ['encrypt']);
  const ct = new Uint8Array(await subtle().encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource, tagLength: 128 }, key, accountKey as BufferSource));
  const out = new Uint8Array(60);
  out.set(nonce, 0);
  out.set(ct, 12);
  return out;
}

export async function unwrapAccountKey(wrapped: Uint8Array, kekBytes: Uint8Array) {
  if (wrapped.length !== 60) throw new Error('wrapped key must be 60 bytes');
  const key = await subtle().importKey('raw', kekBytes as BufferSource, 'AES-GCM', false, ['decrypt']);
  return new Uint8Array(await subtle().decrypt(
    { name: 'AES-GCM', iv: wrapped.subarray(0, 12) as BufferSource, tagLength: 128 },
    key, wrapped.subarray(12) as BufferSource));
}

export const newAccountKey = () => crypto.getRandomValues(new Uint8Array(32));

/** One row per credential that can unlock the account key. Both a passkey and
 *  a recovery code wrap the *same* key (contracts/FORMATS.md §1.2). */
export interface Credential {
  /** 'escrow' is the service holding it — see D-011. */
  method: 'passkey' | 'recovery' | 'escrow';
  version: number;
  /** base64, 32 bytes — the HKDF salt for this credential's KEK. */
  credentialSalt: string;
  /** base64, 60 bytes — nonce(12) || ciphertext(32) || tag(16). */
  wrappedKey: string;
}

// ── recovery code ────────────────────────────────────────────────────────
//
// The only reset path. Shown once, never transmitted, and if it is lost along
// with every passkey the archive is gone — which is what zero-knowledge means
// and has to be said in those words, not softened.

const WORDS = (
  'abacate abelha abrigo agulha alface alicate ancora anzol arame areia armario arroz ' +
  'aveia azeite bacia balde bambu banana bandeja barco batata bexiga bigode boneca bota ' +
  'brinco bule buzina cabide cacto cadeira caixa caju caneca canela capim carvao castanha ' +
  'cebola cenoura cesta chave chuva cigarra cobra colher coqueiro coruja cravo dedal dominó ' +
  'esponja estrela farinha feijao ferro figo flauta floresta fogueira folha formiga fornalha ' +
  'fralda funil gaiola galho garfo gaveta geada girassol goiaba gravata guarda helice hortela ' +
  'igreja ilha janela jarra jiboia joelho lagoa lampada lapis laranja leque limao linho lousa ' +
  'macaco machado madeira mamao manga marreta melancia mochila moeda molho montanha morango ' +
  'muleta navalha neblina ninho novelo nuvem oceano oleo ombro ovelha palha panela papel ' +
  'parafuso pedra peneira pepino pereira pilha pincel pinha planta porta pulseira quadro queijo ' +
  'quintal rabanete raiz relogio remo rodela rolha rosa sabao sacola salsa sandalia semente ' +
  'serra sino sofa tapete telha tesoura tijolo tomate torneira trilho tucano uva vagem vareta ' +
  'vassoura vela vento vidro violao xicara zebra'
).split(' ');

/** 24 words over a 160-word list is ~176 bits, then stretched to 256 by HKDF.
 *  Portuguese because the first cohort is Brazilian and a recovery code you
 *  cannot read back over the phone is not a recovery code. */
export function generateRecoveryCode(): { words: string[]; entropy: Uint8Array } {
  const idx = crypto.getRandomValues(new Uint32Array(24));
  const words = Array.from(idx, (n) => WORDS[n % WORDS.length]!);
  // A word repeating is expected, not a fault: these are 24 independent draws
  // from 162 words, which is where the 176 bits comes from.
  return { words, entropy: canonicalRecoveryBytes(words) };
}

/**
 * Canonical form of a recovery code, as bytes to be digested.
 *
 * Accents are folded away and the form is normalised before anything is
 * derived from it. One word in the list — `dominó` — carries an accent, and a
 * person typing a recovery code back is the least controlled input in the
 * system: they may type `domino`, their keyboard may emit a precomposed
 * character (NFC) and a paste may emit a decomposed one (NFD). Those are three
 * different byte strings for the same word, and without this they would derive
 * three different keys, two of which open nothing. A recovery code that
 * depends on an input method is not a recovery code.
 *
 * Not exported, and not called `entropy`: it returns the words, not a secret
 * derived from them. Use deriveRecoveryEntropy.
 */
function canonicalRecoveryBytes(words: string[]): Uint8Array {
  const normalised = words
    .map((w) => w.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase())
    .join(' ');
  return enc.encode(`wa-archive/v1/recovery\n${normalised}`);
}

export async function deriveRecoveryEntropy(words: string[]): Promise<Uint8Array> {
  const seed = canonicalRecoveryBytes(words);
  return new Uint8Array(await subtle().digest('SHA-256', seed as BufferSource));
}

const fold = (w: string) =>
  w.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
const FOLDED = new Set(WORDS.map(fold));

export function validateRecoveryCode(words: string[]): boolean {
  return words.length === 24 && words.every((w) => FOLDED.has(fold(w)));
}
